import { CTAToken } from "@hermes/api-model-editorial";
import {
    DisplayMode as ProductDisplayMode,
    LabelName,
    QuickBuyProperties,
} from "@hermes/api-model-product";
import { VtoType } from "@hermes/api-model-virtual-try-on";
import { ProductCategory } from "@hermes/states/personalization-page";

export type DisplayMode = "grid" | "list";
export type PriceType = "indicative" | "fixed";

/* eslint-disable @typescript-eslint/naming-convention */
export enum Sort {
    relevance = "relevance",
    priceasc = "priceasc",
    pricedsc = "pricedsc",
}

export enum GridType {
    search = "search",
    category = "category",
    facet = "facet",
}

export interface GridSortByOption {
    sort: Sort;
    label: string;
}

export const DEFAULT_SORT = Sort.relevance;
export interface PriceRangeCurrent {
    valueMinCurrent: number;
    valueMaxCurrent: number;
}

export interface PriceRange extends PriceRangeCurrent {
    valueMin: number;
    valueMax: number;
    valueChanged?: string;
}

export const FH_RANGE_PRICE_PARAM = "fh_range_price";
export const FH_START_INDEX_PARAM = "fh_start_index";
export const FH_SORT_BY_REGEX = /&fh_sort_by=([^&]+)/gm;
export const FH_LOCATION_REGEX = "fh_location";

export interface AltPaths {
    [locale: string]: string;
}

export interface Facet {
    facetName: string;
    facetNameValue: string;
    combinedFacets: CombinedFacets[];
    facetDisplayName: string;
    facetTitle: string;
    updateMetas: boolean;
    altPaths?: AltPaths;
    missingFacet?: boolean;
    enabled: boolean;
    path: string;
}

export interface CombinedFacets {
    facetName: string;
    facetNameValue: string;
}

export interface SelectedFilter {
    name: string;
    value: string;
}

export interface GridCategory {
    pimCode: string;
    name: string;
    masterCategoryName: string;
}

export interface GridCategoryMetas {
    code: string;
    lang: string;
    path: string;
    title: string;
    country: string;
    altPaths: AltPaths;
    description: string;
    textOnGrid?: string;
}

export interface GridMenuLevel {
    menuLv1: string;
    menuLv2?: string;
    menuLv3?: string;
}

/**
 * this interfaces represent objet class model of product Grid to avoid manipulate complex objects on this feature.
 *
 * Equivalents :
 * - GridData <=> ProductCollecionResponse
 * - GridFilter <=> Filter
 * - GridFilterGroup <=>  FilterGroup
 * - GridProduct <=> Product
 * - GridEdito <=> Edito
 * - GridAlternatives <=> Alternatives
 * - GridFacet <=> Facet
 */

export interface GridFilter {
    name: string;
    selected: boolean;
    total: number;
    urlParams?: string;
    value: string;
    filterGroupName: string;
    facetIsCrawlable: boolean;
    combinedFilterImage?: string;
}

export interface GridFilterGroup {
    name: string;
    attribute: string;
    displayHint: string;
    presentation: string;
    isGridHighlight: boolean;
    facetUrlParameterName: string;
    filters: GridFilter[];
    priceRange?: PriceRange;
}

export interface GridAsset {
    url: string;
    title: string;
    type: string;
    posterUrl?: string;
    tag?: string;
    codeRgb?: string;
}

export interface GridProduct {
    sku: string;
    title: string;
    productCode: string;
    departmentCode: string;
    persoProductType: ProductCategory;
    slug: string;
    url: string;
    price: number;
    priceType: PriceType;
    familyCode: string;
    color: string;
    size: string;
    divisionCode: string;
    personalize: boolean;
    massVolume: string;
    hasMoreColors: boolean;
    avgColor: string;
    isColorless: boolean;
    stock: {
        displayOnly: boolean;
        ecom: boolean;
        retail: boolean;
        hasVariantInEcomStock: boolean;
    };
    assets: GridAsset[];
    isHero?: boolean;
    isVto: boolean;
    vtoType?: VtoType;
    isEngravable: boolean;
    productSetType: string;
    labelDisplayOnly?: LabelName;
    displayMode?: ProductDisplayMode;
    gridAssetsVariants: GridAsset[];
    templateType: string;
    quickBuyProperties?: QuickBuyProperties;
    displayNameList?: string;
}

export interface GridAlternatives {
    originalSearch: string;
    resultsFor: string;
    total: number;
}

export interface GridFacet {
    title: string;
    name: string;
    path: string;
    altPaths: AltPaths;
    error?: string;
}

/**
 * type data structure that represent a ProductCollectionResponse object
 */
export interface GridData {
    total: number;
    products: GridProduct[];
    filterGroups: GridFilterGroup[];
    editos: GridEdito[];
    alternatives: GridAlternatives;
    redirectionUrl: string;
    url: string;
    facet: GridFacet;
    isHeroProduct: boolean;
}

export interface GridEdito {
    nodeId: number;
    mediaType: string;
    contentTitle: string;
    contentBody: string;
    nodeUrl: string;
    imageDesktopUrl: string;
    imageDesktopAlt: string;
    imageMobileUrl: string;
    imageMobileAlt: string;
    videoUrl: string;
    videoAlt: string;
    videoLoopMuteAutoplay: boolean;
    alwaysShowControls: boolean;
    imageRetouched: boolean;
    token?: CTAToken;
}
export interface GridFilterTag {
    id: string;
    filter: GridFilter;
    gridFilterGroupName: string;
    priceRange?: PriceRange;
}

export type PageType = "product-page" | "grid-page" | "other-page";
export const PRODUCT_PAGE = "product-page";
export const GRID_PAGE = "grid-page";
export const OTHER_PAGE = "other-page";
